// The toasts on screen as plain data (docs/plans/toast.md): what a new one
// does to the list, how long each kind stays, and which ones show. The
// component in toast.tsx only draws this and runs the clocks.

export type ToastTone = 'success' | 'error' | 'warning' | 'info' | 'loading'

/** A button in the toast, e.g. "Undo". */
export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  /** A second line: why it happened, or what to do. */
  detail?: string
  action?: ToastAction
  /** Stays until it is dismissed or answered (an error that needs a decision). */
  sticky?: boolean
  /** How long it stays, in ms, when the usual time does not fit. */
  duration?: number
}

export interface ToastItem extends ToastOptions {
  id: number
  tone: ToastTone
  title: string
  /** How many times in a row this was said: shown as "×4" from 2. */
  count: number
  /** Changes whenever the clock must start again (a repeat, new words). */
  stamp: number
  /** On its way out: still drawn for the exit, no longer counted. */
  leaving?: boolean
}

export const MAX_SHOWN = 3
export const SHORT_MS = 3500
export const LONG_MS = 7000

/** How long a toast stays, or null for one that waits to be dismissed. */
export function lifetime(
  t: Pick<ToastItem, 'tone' | 'sticky' | 'duration' | 'action'>,
): number | null {
  if (t.sticky || t.tone === 'loading') return null
  if (t.duration !== undefined) return t.duration
  return t.tone === 'error' || t.action ? LONG_MS : SHORT_MS
}

function sameWords(a: ToastItem, tone: ToastTone, title: string, detail?: string): boolean {
  return !a.leaving && a.tone === tone && a.title === title && (a.detail ?? '') === (detail ?? '')
}

/**
 * A new toast. The same words as one still showing make that one count up and
 * start its clock again, so four "Supplier added" are one toast, not four.
 * Slow-work toasts are never merged: each belongs to its own job.
 */
export function push(
  list: readonly ToastItem[],
  id: number,
  tone: ToastTone,
  title: string,
  options: ToastOptions = {},
): { list: ToastItem[]; id: number } {
  const again =
    tone === 'loading' ? undefined : list.find((t) => sameWords(t, tone, title, options.detail))
  if (again) {
    return {
      id: again.id,
      list: list.map((t) =>
        t === again ? { ...t, ...options, count: t.count + 1, stamp: t.stamp + 1 } : t,
      ),
    }
  }
  return { id, list: [...list, { ...options, id, tone, title, count: 1, stamp: 0 }] }
}

/** New words or a new tone for a toast already showing (slow work that finished). */
export function update(
  list: readonly ToastItem[],
  id: number,
  patch: Partial<Pick<ToastItem, 'tone' | 'title'>> & ToastOptions,
): ToastItem[] {
  return list.map((t) => (t.id === id && !t.leaving ? { ...t, ...patch, stamp: t.stamp + 1 } : t))
}

/** Starts a toast's exit; `remove` takes it away once the exit has played. */
export function leave(list: readonly ToastItem[], id: number): ToastItem[] {
  return list.map((t) => (t.id === id ? { ...t, leaving: true } : t))
}

export function remove(list: readonly ToastItem[], id: number): ToastItem[] {
  return list.filter((t) => t.id !== id)
}

/**
 * The toasts to draw, oldest first: the newest three that are staying, plus any
 * on their way out. `more` is how many older ones wait behind them.
 */
export function shown(list: readonly ToastItem[]): { visible: ToastItem[]; more: number } {
  const staying = list.filter((t) => !t.leaving)
  const keep = new Set(staying.slice(-MAX_SHOWN).map((t) => t.id))
  return {
    visible: list.filter((t) => t.leaving || keep.has(t.id)),
    more: Math.max(0, staying.length - MAX_SHOWN),
  }
}

/** The newest toast that is not already leaving (what Escape dismisses). */
export function newest(list: readonly ToastItem[]): ToastItem | undefined {
  return [...list].reverse().find((t) => !t.leaving)
}
